import React from 'react';
import { formatString } from '@deadpan/shared';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';

export const AceAttorneyReveal: React.FC = () => {
  const { socket, gameState, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const copy = t.games.aceAttorney;

  if (!gameState || !currentRoom) {
    return null;
  }

  const isHost = currentRoom.hostId === myPlayerId;
  const setter = currentRoom.players.find((player) => player.id === gameState.aceAttorneySetterId);
  const answers = gameState.aceAttorneyAnswers ?? [];

  const handleNextRound = () => {
    if (!socket || !isHost) return;
    socket.emit('client:ace-attorney-next-round');
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20">
      <div className="text-center mb-4">
        <p className="text-xs sm:text-sm font-semibold text-amber-700">
          {formatString(copy.roundLabel, { round: String(currentRoom.currentRound || 1) })}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent mt-1">
          {copy.revealTitle}
        </h2>
        <p className="text-sm text-amber-800 mt-1">
          {copy.setterLabel}: {setter?.name ?? copy.setterLeft}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div className="p-3 sm:p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
          <p className="text-xs sm:text-sm font-bold text-amber-800 mb-1">{copy.questionLabel}</p>
          <p className="text-base sm:text-lg text-gray-900 whitespace-pre-wrap break-words">
            {gameState.aceAttorneyQuestion}
          </p>
        </div>
        <div className="p-3 sm:p-4 rounded-xl border-2 border-orange-300 bg-orange-50">
          <p className="text-xs sm:text-sm font-bold text-orange-800 mb-1">{copy.judgmentLabel}</p>
          <p className="text-base sm:text-lg text-gray-900 whitespace-pre-wrap break-words">
            {gameState.aceAttorneyJudgment}
          </p>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-800 mb-2">{copy.answersHeading}</p>
          <ul className="flex flex-col gap-2">
            {answers.map((entry) => (
              <li key={entry.playerId} className="p-3 rounded-xl border border-stone-200 bg-stone-50">
                <p className="text-sm font-semibold text-amber-900">{entry.name}</p>
                <p className="text-base text-gray-900 whitespace-pre-wrap break-words mt-1">{entry.text}</p>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-sm text-gray-600 text-center">{copy.revealNote}</p>
        {isHost ? (
          <button
            onClick={handleNextRound}
            className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 rounded-xl font-bold"
          >
            {copy.nextRound}
          </button>
        ) : (
          <p className="text-sm sm:text-base text-center text-gray-700">{copy.waitingForHost}</p>
        )}
      </div>
    </div>
  );
};
